import { PropsWithChildren, useEffect, useState } from 'react';
import { match, P } from 'ts-pattern';
import { useIsLoading } from '~/utils/routing';
import { NonEmptyTuple } from '~/utils/types';
import Card from './Card';
import Spinner from './Spinner';

// ? wonder if this should have a status role, etc for accessibility though spinner does have some
function LoadingCard({
  messages,
  interval = 2000,
}: {
  messages?: string | string[];
  interval?: number;
}) {
  const messageArray = match(messages)
    .returnType<NonEmptyTuple<string>>()
    .with(P.nullish.or([]), () => ['Working on it'])
    .with(P.string, (msg) => [msg])
    .with([P.string, ...P.array()], (msgs) => msgs)
    .exhaustive();

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentMessageIndex(
        (prevIndex) => (prevIndex + 1) % messageArray.length,
      );
    }, interval);

    if (messageArray.length < 2) clearInterval(timer);

    return () => {
      clearInterval(timer);
    };
  }, [messageArray.length, interval]);

  const message = messageArray[currentMessageIndex];

  return (
    <Card className="absolute inset-0 flex items-center justify-center text-center">
      <Spinner className="size-12" />
      <div className="flex flex-col gap-2">
        <p className="subheading-lg text-on-surface">{message}&hellip;</p>
        <p className="text-on-surface-variant">
          Please don&apos;t navigate away or close this page.
        </p>
      </div>
    </Card>
  );
}

type Props = PropsWithChildren<{
  isLoading?: boolean;
  loadingMessages?: string | string[];
  interval?: number;
}>;

/**
 * A boundary component that displays a loading state when loading.
 *
 * This component uses the `useIsLoading` hook to determine if a routing
 * operation is in progress, unless the `isLoading` prop is explicitly provided.
 * When loading, it renders a `LoadingCard` with optional messages; otherwise,
 * it renders its children.
 *
 * Messages can be a single string or an array of strings that will cycle every 2 seconds or a custom interval.
 */
export default function LoadingBoundary({
  children,
  isLoading: isLoadingProp,
  loadingMessages,
  interval,
}: Props) {
  const isRoutingLoading = useIsLoading();
  const isLoading = isLoadingProp ?? isRoutingLoading;

  return (
    <>
      {children}
      {isLoading && (
        <LoadingCard messages={loadingMessages} interval={interval} />
      )}
    </>
  );
}
